import { cva } from 'class-variance-authority';
import { focusRing } from 'components/styles/focusRing';

/**
 * Accordion class recipes.
 *
 * Like `Button.recipe` and `TextArea.recipe`, these contain ZERO `dark:` variants — colour comes
 * entirely from the `--ds-accordion-*` slots that `.ds-accordion` sets in
 * `components/styles/design-system.css`, and that class needs no dark rule at all because every
 * slot it sets names a semantic role and a role already carries a value per theme.
 *
 * ── Ported from Figma 1447:135303 on 2026-09-18 ────────────────────────────────────────────────
 *
 * An earlier revision of this file recorded that there was no Design System 2.0 drawing of this
 * component and that its structure and colour were "open to the design review". That review has
 * happened: the set exists, it carries a written description that names this directory, and the
 * geometry below is read off the fifty cells rather than chosen. Where a value here disagrees with
 * the previous implementation, the drawing is the reason.
 *
 * The five axes the set draws, and where each one is answered:
 *
 *   Surface   colour only — `data-surface` on the root, resolved to slots in `design-system.css`.
 *   Layout    geometry only — a `layout` variant on the recipes below.
 *   State     the trigger's hover, active and focus-visible pseudo-classes, painted on the item.
 *   isOpen    `[data-open]` on the item; the trigger uses `[data-panel-open]` for its child glyphs.
 *   isDisabled `[data-disabled]`, likewise. See the note further down on where it arrives from.
 *
 * Nothing is set by two of them, which is the set description's own claim and is worth preserving:
 * it is what lets each axis be a flat variant rather than a matrix.
 *
 * ── Where the state comes from, and why it is not `TextArea`'s shape ───────────────────────────
 *
 * The field pair reads `Field.Root`'s `data-focused` / `data-filled` / `data-invalid` through a
 * `group/ti` marker, because there the element that KNOWS the state and the element that draws it
 * are different. Here the trigger is the control, so its own pseudo-classes are the state, and the
 * rules take Button's guard form:
 *
 *   [&:not([data-disabled]):not(:active):hover]   hover, excluded from pressed and disabled
 *   [&:not([data-disabled]):active]               pressed
 *   [&[data-disabled]]                            disabled
 *
 * The guards matter for exactly the reason `Button.recipe` spells out: a MOUSE press matches
 * `:hover` and `:active` at once, the two rules compile to the same specificity, and without
 * `:not(:active)` the winner is decided by whatever order Tailwind happens to emit them in.
 *
 * Two groups are declared. `group/accordion` is on the root and carries the three container-level
 * choices — surface, layout and icon placement — down to the parts that need them as selectors
 * rather than as props. `group/accordion-trigger` is on the trigger and is read by the indicator,
 * the title and the description, which are children with no state of their own.
 *
 * ── The `data-disabled` the trigger carries ───────────────────────────────────────────────────
 *
 * Base UI 1.7.0 builds the trigger with `focusableWhenDisabled: true`, so a disabled row is NOT
 * given the native `disabled` attribute: it keeps its place in the tab order and is marked with
 * `aria-disabled` instead. That is the accessible behaviour — a control that vanishes from the tab
 * order is a control a keyboard user cannot discover the state of — and it is also why every rule
 * below keys off `data-disabled` rather than `:disabled`, which would never match.
 *
 * ── How disabled is drawn, which changed in this port ──────────────────────────────────────────
 *
 * The previous implementation swapped the card's fill to `--background-disabled` and its rim to
 * `--border-control-disabled`, then dropped the title and icon to `--foreground-disabled`. The set
 * does none of that: the container KEEPS its enabled fill and rim, and the CONTENT drops to 50%
 * opacity.
 *
 * That is not a shortcut, and the set description gives the measurement behind it:
 * `background/disabled` is opaque and tuned to a page-level host, so on an on-surface card it
 * measures 1.26:1 in dark — HEAVIER than the enabled fill, i.e. the "disabled" treatment made the
 * card more prominent — and 1.00:1 in light, i.e. invisible. One opacity value works on every host
 * in both modes, and it also fades a `Tag` in the leading slot without needing a disabled Tag cell,
 * which the Tag set does not have.
 *
 * WCAG 1.4.3 exempts disabled controls from the contrast floor, so the 50% is legitimate rather
 * than tolerated. `accordionContrast.test.ts` asserts the exemption explicitly rather than
 * measuring through it.
 */

/**
 * The list.
 *
 * The `.ds-accordion` slot class lives HERE rather than on the item, for the reason
 * `textAreaRootRecipe` documents: custom properties cascade, so everything inside inherits them,
 * and anything that later renders outside the item — a list-level empty state, say — would
 * otherwise fall outside their scope and resolve to inherited colour rather than failing visibly.
 *
 * `group/accordion` carries surface, layout and icon placement down to the parts, so those choices
 * are made once on the container and read as selectors rather than threaded through React context.
 * That keeps them in the recipe layer, which is where a visual decision belongs.
 *
 * ── The gap, and the one surface that has none ─────────────────────────────────────────────────
 *
 * 8px between items for the two boxed surfaces, which is the set description's own instruction
 * ("stack instances 8px apart (x2) to make a group") and also what the previous implementation
 * used. The gap rather than a collapsed shared seam: the seam is the stacked-FIELD contract, and it
 * is wrong here, because a field group is one control split into rows while an accordion list is
 * several independent cards. The gap also gives the whole-item focus shadow somewhere to be drawn —
 * see `accordionItemRecipe`.
 *
 * `surface="none"` takes NO gap, because it has no cards to separate. The set's rule is that such a
 * group takes a `Divider` between rows instead of more air, and the divider has to be supplied by
 * the caller — a component that cannot see its own siblings cannot interleave anything between
 * them. Two cautions from the set, both worth repeating at the call site: use the reviewed
 * `Divider`, which binds the translucent `--border-on-surface` and so is visible where
 * `--border-primary` would not be; and zero its horizontal padding, which is 12px despite its own
 * description claiming full-bleed.
 */
export const accordionRootRecipe = cva(['ds-accordion group/accordion', 'flex w-full flex-col'], {
  variants: {
    surface: {
      none: 'gap-0',
      'on-page': 'gap-[8px]',
      'on-surface': 'gap-[8px]',
    },
  },
  defaultVariants: { surface: 'on-surface' },
});

/**
 * One section: the card that holds a row and its panel.
 *
 * NOT `overflow-hidden`, which is the obvious thing to reach for on a rounded box containing an
 * animating child and is wrong twice over. The clipping the height animation needs belongs on the
 * panel, which is the element whose height changes; and clipping here would cut the item's focus
 * shadow off at the card's edge, leaving a focus indicator visible on three sides.
 *
 * ── Radius ────────────────────────────────────────────────────────────────────────────────────
 *
 * Read off the set rather than borrowed from `TextArea` as the previous revision's 16px was:
 * `radius/lg` (24px) for stacked, `radius/md` (16px) for inline, none at all for `surface="none"`.
 * The two boxed radii pair with the two insets — the taller card with the wider inset — which is
 * why the axis is geometry and not decoration.
 *
 * ── The rim, and the ladder its four states climb ─────────────────────────────────────────────
 *
 * The open cells implement the set's written rule: on-surface steps both fill and rim from the
 * default rung to hover, while on-page keeps its opaque fill and steps only its rim. These values
 * are separate slots because open is persistent state, unlike the translucent interaction layer.
 *
 * The rim ALSO steps on hover and active, which the set draws on every one of its eight boxed
 * variant groups and which its description does not mention — the drawing is the spec here, agreed
 * 2026-09-21. So the resolved rim is the higher of the open rung and the interaction rung:
 *
 *   rest      --ds-accordion-stroke
 *   open      --ds-accordion-stroke-open     ┐ one value on both boxed surfaces
 *   hover     --ds-accordion-stroke-hover    ┘
 *   active    --ds-accordion-stroke-active     outranks open, so pressed-while-open reads pressed
 *   focus     unchanged — the shared halo carries focus on its own
 *
 * Source order is not what resolves this. `:has(…:hover)` carries the specificity of its argument,
 * so both interaction rules already outrank `[data-open]`, and hover carries `:not(:active)` so the
 * two can never both match. That mutual exclusion is the same guard the state layer above uses, and
 * it is deliberate: active is the LESS specific of the pair and would otherwise lose.
 */
export const accordionItemRecipe = cva(
  [
    'relative flex w-full flex-col',
    'isolate',
    'transition-[border-color,background-color] duration-[var(--motion-control)] ease-[var(--motion-control-ease)] motion-reduce:transition-none',
    'before:pointer-events-none before:absolute before:opacity-0 before:content-[" "]',
    'before:transition-[background-color,border-color,opacity] before:duration-[var(--motion-control)] before:ease-[var(--motion-control-ease)] motion-reduce:before:transition-none',
    '[&:has(>[data-part=header]>[data-part=trigger]:not([data-disabled]):not(:active):hover)::before]:bg-[var(--ds-accordion-state-hover)]',
    '[&:has(>[data-part=header]>[data-part=trigger]:not([data-disabled]):not(:active):hover)::before]:opacity-100',
    '[&:has(>[data-part=header]>[data-part=trigger]:not([data-disabled]):active)::before]:bg-[var(--ds-accordion-state-active)]',
    '[&:has(>[data-part=header]>[data-part=trigger]:not([data-disabled]):active)::before]:opacity-100',
    focusRing('accordionItem'),
    '[&[data-disabled]>[data-part=header]]:opacity-50',
    '[&[data-disabled]>[data-part=panel]>[data-part=panel-content]]:opacity-50',
  ],
  {
    variants: {
      layout: {
        inline: 'rounded-[16px] before:rounded-[15px]',
        stacked: 'rounded-[24px] before:rounded-[23px]',
      },
      surface: {
        // The card is gone but the state layer keeps the inline chip's geometry, so the rim it
        // gains on hover and active belongs to `::before` rather than to the item.
        //
        // The item keeps the layout radius even with nothing to draw, because the focus halo is a
        // box-shadow and traces the item's own corners — the set's no-surface root keeps its 16px
        // for the same reason. Note the set does NOT draw a focus ring on this surface at all: its
        // no-surface Focus cells have no ring carrier, which for a row that is a real button is an
        // omission in the drawing rather than a decision, so the shared halo stays on here.
        none: [
          'border-0 bg-transparent before:inset-[1px]',
          'before:border-solid before:border-[length:var(--ds-accordion-stroke-width)] before:border-transparent',
          '[&:has(>[data-part=header]>[data-part=trigger]:not([data-disabled]):not(:active):hover)::before]:border-[var(--ds-accordion-stroke-hover)]',
          '[&:has(>[data-part=header]>[data-part=trigger]:not([data-disabled]):active)::before]:border-[var(--ds-accordion-stroke-active)]',
        ],
        'on-page': '',
        'on-surface': '',
      },
    },
    compoundVariants: [
      {
        surface: ['on-page', 'on-surface'],
        class: [
          'border-solid border-[length:var(--ds-accordion-stroke-width)] border-[var(--ds-accordion-stroke)]',
          'bg-[var(--ds-accordion-bg)]',
          'before:inset-[1px]',
          '[&[data-open]]:bg-[var(--ds-accordion-bg-open)]',
          '[&[data-open]]:border-[var(--ds-accordion-stroke-open)]',
          '[&:has(>[data-part=header]>[data-part=trigger]:not([data-disabled]):not(:active):hover)]:border-[var(--ds-accordion-stroke-hover)]',
          '[&:has(>[data-part=header]>[data-part=trigger]:not([data-disabled]):active)]:border-[var(--ds-accordion-stroke-active)]',
        ],
      },
    ],
    defaultVariants: { layout: 'inline', surface: 'on-surface' },
  }
);

/**
 * The heading that wraps the trigger.
 *
 * Base UI renders an `<h3>`, which is a real heading in the document outline — that is the whole
 * point of the part, since it is what lets a screen reader user jump between sections. It carries
 * no type styling of its own: the visible text is the trigger's, and styling the heading would
 * fight it. `m-0` is load-bearing rather than tidiness, for the reason the field's helper row
 * gives: Preflight is absent from the token layer, so an `<h3>` keeps the browser's default
 * `margin: 1em 0` and the card would open with 21px of empty space above and below its row.
 */
export const accordionHeaderRecipe = cva(['relative z-[1] m-0 flex']);

/**
 * The row, which is the control.
 *
 * It fills the card and pays all the padding itself, the same decision the field's control makes
 * and for the same reason: pad the card instead and the button becomes a strip across the middle,
 * so most of what looks like the row is dead to the pointer. Here the whole row is meant to open
 * the section, so every pixel of it has to be the button. In `layout="stacked"` that means BOTH
 * rows — the eyebrow and the title beneath it — are inside one button.
 *
 * ── The resets ────────────────────────────────────────────────────────────────────────────────
 *
 * Preflight is deliberately absent from the token layer, so the browser's own `<button>` styling
 * is still in play: a grey bevelled background, a 2px outset border, centred 13.33px Arial and
 * `text-align: center`. Every one of those is overridden explicitly below. `text-left` is the one
 * that is easy to miss and the most visible when missed — a centred section title does not line up
 * with the panel text underneath it.
 *
 * ── The insets, and the bottom one that moves ─────────────────────────────────────────────────
 *
 * Read off the set: `x6` (24px) all round for stacked, `x4` (16px) for inline, and `surface="none"`
 * overriding both to `x2` (8px) horizontally and `x3` (12px) vertically.
 *
 * The bottom inset SHRINKS to the 12px inter-row gap while the panel is open, and that is arithmetic
 * from the cells rather than a flourish. A closed stacked card measures 168px: 1 + 24 + 118 + 24 +
 * 1. Its open sibling measures 180px: 1 + 24 + 118 + 12 + 0 + 24 + 1 — the header is unchanged and
 * the 24px that was the trigger's bottom padding has become a 12px gap, with the 24px reappearing
 * below the panel's content. Inline works the same way from 16px. `surface="none"` keeps its 12px
 * bottom inset and adds an 8px gap, so nothing moves there.
 *
 * Focus is intentionally absent here. The latest set draws the shared two-shadow Focused effect
 * around the whole item, so `accordionItemRecipe` promotes and rings the parent when its direct
 * trigger is focus-visible. A nested button in panel content must not ring the accordion item.
 *
 * ── Radius ────────────────────────────────────────────────────────────────────────────────────
 *
 * The card's radius less its 1px border — 23px stacked, 15px inline — which is the inner radius
 * that leaves the hover fill concentric with the edge rather than showing a sliver of card colour
 * at each corner. The bottom pair is squared off while the panel is open, because the row is then
 * the top half of the card and a rounded bottom edge would cut a notch out of the panel above its
 * own content.
 *
 * ── Alignment ─────────────────────────────────────────────────────────────────────────────────
 *
 * `items-start` for inline, not `items-center`. The set aligns the indicator, the leading tag and
 * the title block to the row's TOP, which is the only alignment that survives a description: centre
 * the row and a two-line description drags the caret down to the middle of a block it is supposed
 * to be pointing at. The previous implementation centred them, which was right for a row that could
 * only ever be one line tall.
 */
export const accordionTriggerRecipe = cva(
  [
    'group/accordion-trigger',
    'm-0 flex w-full appearance-none border-0 bg-transparent text-left outline-none',
    'font-sans',
    'cursor-pointer select-none',
    'transition-[color,padding-bottom] duration-[var(--motion-control)] ease-[var(--motion-control-ease)] motion-reduce:transition-none',

    '[&[data-disabled]]:cursor-not-allowed',
  ],
  {
    variants: {
      layout: {
        inline: 'flex-row items-start gap-[12px] rounded-[15px]',
        stacked: 'flex-col items-stretch gap-[36px] rounded-[23px]',
      },
      surface: {
        none: 'rounded-none px-[8px] py-[12px]',
        'on-page': '',
        'on-surface': '',
      },
    },
    compoundVariants: [
      {
        layout: 'inline',
        surface: ['on-page', 'on-surface'],
        class: ['p-[16px]', 'data-[panel-open]:rounded-b-none data-[panel-open]:pb-[12px]'],
      },
      {
        layout: 'stacked',
        surface: ['on-page', 'on-surface'],
        class: ['p-[24px]', 'data-[panel-open]:rounded-b-none data-[panel-open]:pb-[12px]'],
      },
    ],
    defaultVariants: { layout: 'inline', surface: 'on-surface' },
  }
);

/**
 * The eyebrow row, `layout="stacked"` only — Figma's `Row_Meta`.
 *
 * The leading slot on one side and the actions on the other, 32px tall because that is the height
 * of the `Tag` the set puts there. Centred rather than top-aligned, unlike the inline row: this row
 * holds only single-height objects, so there is no two-line case to align against.
 */
export const accordionMetaRowRecipe = cva(['flex w-full items-center gap-[12px]']);

/**
 * The trailing cluster inside the eyebrow row — Figma's `Actions`.
 *
 * `flex-1` with `justify-end`, so it takes whatever the leading slot leaves and pushes its contents
 * against the card's inner edge. `min-w-0` for the reason the title block needs it: a flex item
 * refuses to shrink below its content by default, and a long `trailing` value would otherwise push
 * the leading tag out through the opposite edge.
 */
export const accordionActionsRecipe = cva(['flex min-w-0 flex-1 items-center justify-end gap-[12px]']);

/**
 * The open/closed indicator's 20px box.
 *
 * A 20px wrapper around a 16px glyph, which is the set's `Indicator_Leading` and
 * `Indicator_Trailing` frames exactly. The 2px of slack on each side is not decoration: it is what
 * the inline panel content's 32px inset is computed from — the wrapper's 20 plus the row's 12 gap —
 * so the columns line up by construction rather than by a number someone chose.
 *
 * Reads the trigger's state through `group/accordion-trigger`, being a child with no state of its
 * own, which is what a group is for.
 *
 * ── No rotation ───────────────────────────────────────────────────────────────────────────────
 *
 * The previous implementation drew one `CaretDown` and rotated it 180deg to mean open. The set
 * draws four distinct glyphs and rotates nothing — `Plus` becoming `X` on stacked, `CaretRight`
 * becoming `CaretDown` on inline — and its rules name the change explicitly ("Code rotates a
 * CaretDown instead and needs changing"). The swap is done in `Accordion.tsx`, so there is no
 * transform here at all.
 *
 * That loses an animated transition between the two states, and the loss is deliberate: a caret
 * that rotates and a plus that becomes a cross are different gestures, and cross-fading two
 * different paths costs more than the cue is worth. The indicator's COLOUR still transitions, which
 * is what carries hover and disabled.
 */
export const accordionIndicatorRecipe = cva(
  [
    'flex size-[20px] shrink-0 items-center justify-center',
    'text-[var(--ds-accordion-icon)]',
    'transition-[color] duration-[var(--motion-control)] ease-[var(--motion-control-ease)] motion-reduce:transition-none',
  ],
  {
    variants: {
      /**
       * Only the inline arrangement can move: the stacked indicator's place is the eyebrow row's
       * trailing edge, which is a different element entirely, so there is nothing to order.
       *
       * The rule keys off `end`, NOT `start`, and the asymmetry is the whole subtlety. The DOM
       * order here is the SET's — indicator, leading, title, trailing — so the glyph is already
       * first and `start` needs no rule at all. It was written the other way round first, keyed off
       * `start` with `order-[-1]`, which is a no-op on an element that is already first: the caret
       * rendered on the leading edge for BOTH values of the prop, and every unit test passed
       * because they assert the attribute and the rule rather than the geometry. The `IconPlacement`
       * story's `play` function measures the two boxes, and a browser screenshot is what actually
       * caught it.
       *
       * `order-[1]` rather than `order-last`, which Tailwind v4 compiles to
       * `order: calc(infinity)`. One explicit step above the default 0 is all that is needed to put
       * the glyph after three siblings that never set `order` themselves, and it is a value a reader
       * can evaluate.
       */
      layout: {
        inline: 'group-data-[icon-placement=end]/accordion:order-[1]',
        stacked: '',
      },
    },
    defaultVariants: { layout: 'inline' },
  }
);

/** The leading slot's wrapper — a `Tag` by default, so it must not be allowed to shrink. */
export const accordionLeadingRecipe = cva(['flex shrink-0 items-center']);

/** The trailing slot's wrapper. Non-interactive content only — see `types.ts` for why. */
export const accordionTrailingRecipe = cva(['flex shrink-0 items-center']);

/**
 * The title and description as one block — Figma's `Titles`.
 *
 * `min-w-0` is what lets a long title wrap instead of forcing the row wider than the card: a flex
 * item's default `min-width: auto` refuses to shrink below its content, and the indicator would be
 * pushed out through the edge. The title wraps rather than truncating, because a section title is
 * the only thing naming the content behind it and half of one is not a name.
 *
 * 4px between the two lines, which is `x1` in the set.
 */
export const accordionTitlesRecipe = cva(['flex min-w-0 flex-1 flex-col gap-[4px] text-left']);

/**
 * The title.
 *
 * Two type levels, one per layout, and this is NOT an inconsistency in the set even though it reads
 * like one at first: stacked draws `Heading/xs` (16/26) and inline draws `Label/lg` (14/20).
 * Measured from the cells on 2026-09-18 — the stacked title box is 26px tall and the inline one is
 * 20px — so the set's written rule that "the row title" is `Label/lg` is describing the inline case
 * only.
 *
 * The reason it is coherent: a stacked card is a section with an eyebrow above it and a description
 * below, which is a heading's job, while an inline row is one line in a list, which is a label's.
 * The set's third type level — anything inside the panel — stays at `Body/md`, so the three remain
 * distinct in both layouts.
 */
export const accordionTitleRecipe = cva(
  [
    'text-[var(--ds-accordion-title)]',
    'transition-[color] duration-[var(--motion-control)] ease-[var(--motion-control-ease)] motion-reduce:transition-none',
  ],
  {
    variants: {
      layout: {
        inline: 'text-label-lg',
        stacked: 'text-heading-xs',
      },
    },
    defaultVariants: { layout: 'inline' },
  }
);

/**
 * The description under the title.
 *
 * `Body/md` in both layouts, and secondary rather than primary — it is subordinate to the title it
 * explains, and the set's worst enabled cell is this one at 6.40:1 against a 4.5:1 floor, so the
 * step down is affordable.
 *
 * Secondary and not tertiary, deliberately: this is text a user is meant to READ, and a tertiary
 * foreground is a state signal — disabled, done, empty, or purely decorative — never a way to rank
 * active content. DS 2.0 has no tertiary foreground to reach for anyway, which is the gap that makes
 * the mistake unavailable here; if one ever ships it should be documented that way at birth.
 */
export const accordionDescriptionRecipe = cva([
  'text-body-md text-[var(--ds-accordion-description)]',
  'transition-[color] duration-[var(--motion-control)] ease-[var(--motion-control-ease)] motion-reduce:transition-none',
]);

/**
 * The panel.
 *
 * Height, not max-height, and the value is Base UI's own `--accordion-panel-height` — which it
 * measures and sets as an inline custom property on this element, and leaves as `auto` once the
 * transition has settled. Animating `max-height` to a guessed ceiling is the usual shortcut and it
 * is why so many accordions ease slowly through empty space before the content appears.
 *
 * `data-starting-style` and `data-ending-style` are Base UI's two transition hooks: the first is
 * present for one frame as the panel mounts, the second for the duration of the close. Both pin
 * the height to 0, so the transition runs between 0 and the measured height in each direction. The
 * close hook is also what keeps the content mounted long enough to animate out — Base UI defers
 * the unmount until the animations on this element have finished.
 *
 * `overflow-hidden` belongs here rather than on the card, because this is the element whose height
 * is being clipped.
 *
 * ── Reduced motion ────────────────────────────────────────────────────────────────────────────
 *
 * `motion-reduce:transition-none`, which is not the throwaway it is on the other recipes in this
 * repo. This is the only animated primitive of the five, and an expanding height is a vestibular
 * trigger — WCAG 2.3.3 asks for exactly this. With the transition off the panel snaps to its
 * height instead, and the unmount still happens: Base UI waits for the animations on the element
 * to finish, and an element with no transition has none, so the wait resolves immediately.
 */
export const accordionPanelRecipe = cva([
  'relative z-[1] h-[var(--accordion-panel-height)] overflow-hidden',
  /**
   * `--motion-layout`, which is what this declaration should always have read: a panel expanding is
   * an on-screen element MOVING, not a control changing its own tint, and the role table names
   * exactly that case. It had been left on Tailwind's bare ease-out curve and `--motion-control` as
   * the single allowlisted literal in `motionLiterals.test.ts`, because no curve then available
   * equalled that one and the change is visible.
   *
   * It is taken here, deliberately, and it is the one retime in this change:
   *
   *   duration   150ms -> 200ms. A height expansion travels further than any tint in the set and is
   *              the one declaration whose length a reader actually perceives.
   *   curve      Tailwind's ease-out (`cubic-bezier(0, 0, 0.2, 1)`) -> `--ease-movement`, which is
   *              ease-in-out-cubic. A disclosure is a two-way toggle between two on-screen states,
   *              so it accelerates out of rest and brakes into it. A pure deceleration is the right
   *              curve for something ARRIVING from off-screen, which a panel already in the flow is
   *              not.
   *
   * The allowlist is now empty and the guard carries no exception at all.
   *
   * ── The one layout-tier animation in the set, and why it stays ────────────────────────────────
   *
   * `height` enters the renderer at Layout, so it re-measures every frame — the most expensive tier
   * there is, where every other animated declaration in DS 2.0 is composite-only (`opacity`,
   * `scale`) or paint-tier at worst. The usual swap down a tier is `scale-y`, and it is not taken:
   * scaling a panel distorts the type inside it for the whole 200ms, which is worse than the cost
   * it saves. This is a knowing exception, not an oversight.
   *
   * What keeps it affordable is what is being measured. The height comes from Base UI as an inline
   * custom property on THIS element, set once per open, and the transition then runs on `height`
   * directly. That is not the trap it resembles: an animated custom property on a SHARED PARENT
   * forces a style recalculation on every descendant every frame, which is what makes a long
   * drag-driven list degrade with its item count. Here nothing animates the variable, and the
   * element is the only consumer.
   *
   * No `will-change`. Browsers already promote what they can, a layer costs GPU memory, and the
   * rule is to add it only once a shift or a dropped frame has actually been seen. None has.
   */
  'transition-[height] duration-[var(--motion-layout)] ease-[var(--motion-layout-ease)]',
  'motion-reduce:transition-none',
  'data-[starting-style]:h-0 data-[ending-style]:h-0',
]);

/**
 * The panel's content wrapper.
 *
 * A separate element from the panel, and it has to be. The panel's height is animated to a measured
 * value, so padding on it would be added to that height at every frame and the collapsed state
 * would still be `padding-block` tall rather than zero — the content would disappear into a
 * stubborn empty band. Paying the padding one level in keeps the animated box exactly the height of
 * what is inside it.
 *
 * The top padding is the inter-row gap the trigger's bottom inset shrank to make room for — 12px on
 * the boxed surfaces, 8px on `none` — and it is paid HERE rather than as a `gap` on the item for
 * the same reason the rest of the padding is: a flex gap on the item is paid for the whole duration
 * of the CLOSING animation, while the panel is still mounted at zero height, so the card would hold
 * a 12px band of empty space on its way shut.
 *
 * ── The inline inset ──────────────────────────────────────────────────────────────────────────
 *
 * The inline panel content starts under the TITLE rather than under the caret. Its start inset is
 * the surface inset plus the indicator's 20px wrapper and the row's 12px gap: 48px on a boxed
 * surface and 40px on `none`. Keeping the arithmetic here makes the two values move together if
 * either Figma token changes again.
 *
 * It applies only when the indicator is actually leading. With the default `iconPlacement="end"`
 * there is nothing at the row's start to clear, so the inset would push the content 32px past the
 * title it is supposed to line up with — hence the `group-data` guard rather than a flat value.
 * `layout="stacked"` never takes it: its title starts at the card's own inset, because the
 * indicator is on the row above.
 */
export const accordionPanelContentRecipe = cva(['font-sans text-body-md text-[var(--ds-accordion-panel-fg)]'], {
  variants: {
    layout: {
      inline: '',
      stacked: '',
    },
    surface: {
      none: 'px-[8px] pt-[8px] pb-[12px]',
      'on-page': '',
      'on-surface': '',
    },
  },
  compoundVariants: [
    {
      layout: 'inline',
      surface: ['on-page', 'on-surface'],
      class: 'px-[16px] pt-[12px] pb-[16px] group-data-[icon-placement=start]/accordion:ps-[48px]',
    },
    {
      layout: 'inline',
      surface: 'none',
      class: 'group-data-[icon-placement=start]/accordion:ps-[40px]',
    },
    {
      layout: 'stacked',
      surface: ['on-page', 'on-surface'],
      class: 'px-[24px] pt-[12px] pb-[24px]',
    },
  ],
  defaultVariants: { layout: 'inline', surface: 'on-surface' },
});
